/**
 * Analytics Tracker Hook
 * Tracks page views and maintains session heartbeat
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

export const useAnalytics = () => {
  const location = useLocation();
  const heartbeatInterval = useRef(null);
  const sessionId = useRef(getSessionId());

  // Track page view
  const trackPageView = useCallback(async (page) => {
    try {
      await fetch(`${API_URL}/api/analytics/track/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          session_id: sessionId.current
        })
      });
    } catch (error) {
      // Silent fail - don't break the app for analytics
      console.debug('Analytics tracking failed:', error);
    }
  }, []);

  // Send heartbeat to keep session alive
  const sendHeartbeat = useCallback(async (page) => {
    try {
      await fetch(`${API_URL}/api/analytics/track/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current,
          page
        })
      });
    } catch (error) {
      console.debug('Heartbeat failed:', error);
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    const page = location.pathname + location.search;
    trackPageView(page);

    // Clear existing heartbeat
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    // Start new heartbeat (every 30 seconds)
    heartbeatInterval.current = setInterval(() => {
      sendHeartbeat(page);
    }, 30000);

    // Cleanup on unmount
    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [location, trackPageView, sendHeartbeat]);

  return { sessionId: sessionId.current };
};

export default useAnalytics;
